<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';
import { useDealsStore } from 'dashboard/stores/deals';
import ContactAPI from 'dashboard/api/contacts';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import Timeline from 'dashboard/components-next/Timeline/Timeline.vue';
import ContactCompanySection from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCompanySection.vue';
import DealInfo from 'dashboard/routes/dashboard/conversation/contact/DealInfo.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  isExpanded: { type: Boolean, default: false },
  disableExpand: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  company: { type: Object, default: null },
  companies: { type: Array, default: () => [] },
  identifier: { type: String, default: '' },
  createdAt: { type: [String, Number], default: null },
  lastActivityAt: { type: [String, Number], default: null },
  labels: { type: Array, default: null },
});

const emit = defineEmits([
  'toggle',
  'updateContact',
  'select',
  'avatarHover',
  'nameClick',
]);

const { t } = useI18n();
const store = useStore();
const { relativeTime } = useLocalizedTime();

const contactsFormRef = ref(null);
const avatarFile = ref(null);
const avatarUrl = ref('');

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  phoneNumber: props.phoneNumber,
  companyId: props.company?.id || null,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const avatarSrc = computed(() =>
  avatarUrl.value ? avatarUrl.value : props.thumbnail
);

const createdAtFormatted = computed(() =>
  props.createdAt ? relativeTime(props.createdAt) : ''
);

const lastActivityAtFormatted = computed(() =>
  props.lastActivityAt ? relativeTime(props.lastActivityAt) : ''
);

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    await store.dispatch('contacts/deleteAvatar', props.id);
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    avatarFile.value = null;
    avatarUrl.value = '';
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};

// --- Tabs (Conversas / Empresa / Negociação / Notas) ---
const CONTACT_TABS_OPTIONS = [
  { key: 'HISTORY', value: 'history' },
  { key: 'COMPANY', value: 'company' },
  { key: 'DEALS', value: 'deals' },
  { key: 'NOTES', value: 'notes' },
];

const activeTab = ref('history');
const isHistoryPanelOpen = ref(false);
const dealsStore = useDealsStore();
const contactDealsCount = ref(0);

const tabs = computed(() =>
  CONTACT_TABS_OPTIONS.map(tab => {
    if (tab.value === 'company' && props.companies.length) {
      return {
        label: `${t('CONTACTS_LAYOUT.SIDEBAR.TABS.COMPANY')} (${props.companies.length})`,
        value: tab.value,
      };
    }
    if (tab.value === 'deals' && contactDealsCount.value > 0) {
      return {
        label: `${t('CONTACTS_LAYOUT.SIDEBAR.TABS.DEALS')} (${contactDealsCount.value})`,
        value: tab.value,
      };
    }
    return {
      label: t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${tab.key}`),
      value: tab.value,
    };
  })
);

const activeTabIndex = computed(() =>
  CONTACT_TABS_OPTIONS.findIndex(tab => tab.value === activeTab.value)
);

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const companyName = computed(
  () => props.company?.name || props.additionalAttributes?.companyName || ''
);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};

// --- Contact labels (read-only preview for the collapsed card header) ---
const contactLabelsStore = useContactLabelsStore();
const contactLabels = ref(props.labels || []);

const labelColorMap = computed(() => contactLabelsStore.getLabelColorMap);

const fetchLabels = async () => {
  try {
    const { data } = await ContactAPI.getContactLabels(props.id);
    contactLabels.value = data.payload || [];
  } catch {
    contactLabels.value = [];
  }
};

// --- Deals count (for the "Negociação" tab badge) ---
const fetchDealsCount = async () => {
  try {
    const deals = await dealsStore.getByContact(props.id);
    contactDealsCount.value = deals.length;
  } catch {
    contactDealsCount.value = 0;
  }
};

onMounted(() => {
  contactLabelsStore.fetchAll();
  if (!props.labels) fetchLabels();
  fetchDealsCount();
});

watch(
  () => props.labels,
  labels => {
    if (labels) contactLabels.value = labels;
  }
);

watch(
  () => props.isExpanded,
  expanded => {
    if (!expanded) fetchLabels();
  }
);
</script>

<template>
  <div class="relative">
    <CardLayout
      :key="id"
      layout="row"
      :class="{
        'outline-n-weak !bg-n-slate-3 dark:!bg-n-solid-3': isSelected,
      }"
    >
      <div class="flex items-center justify-start flex-1 gap-4">
        <Checkbox
          v-if="selectable"
          :model-value="isSelected"
          class="shrink-0"
          @change="event => toggleSelect(event.target.checked)"
          @click.stop
        />
        <div
          class="relative"
          @mouseenter="handleAvatarHover(true)"
          @mouseleave="handleAvatarHover(false)"
        >
          <Avatar
            :name="name"
            :src="avatarSrc"
            :size="48"
            :status="availabilityStatus"
            hide-offline-status
            rounded-full
            :allow-upload="isExpanded"
            @upload="handleAvatarUpload"
            @delete="handleAvatarDelete"
          />
        </div>
        <div class="flex flex-col gap-0.5 flex-1">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span
              class="text-base font-medium truncate text-n-slate-12"
              :class="{ 'cursor-pointer hover:underline': disableExpand }"
              @click="disableExpand && emit('nameClick')"
            >
              {{ name }}
            </span>
            <span v-if="companyName" class="inline-flex items-center gap-1.5">
              <span
                class="i-ph-building-light size-4 text-n-slate-10 mb-0.5 shrink-0"
              />
              <span class="text-sm truncate text-n-slate-11">
                {{ companyName }}
              </span>
            </span>
          </div>
          <div
            class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1"
          >
            <div v-if="email" class="truncate max-w-72" :title="email">
              <span class="text-sm text-n-slate-11">
                {{ email }}
              </span>
            </div>
            <div
              v-if="
                email && (phoneNumber || countryDetails || contactLabels.length)
              "
              class="w-px h-3 truncate bg-n-slate-6"
            />
            <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
              {{ phoneNumber }}
            </span>
            <div
              v-if="phoneNumber && (countryDetails || contactLabels.length)"
              class="w-px h-3 truncate bg-n-slate-6"
            />
            <span
              v-if="countryDetails"
              class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
            >
              <Flag :country="countryDetails.countryCode" class="size-3.5" />
              {{ formattedLocation }}
            </span>
            <div
              v-if="countryDetails && contactLabels.length"
              class="w-px h-3 truncate bg-n-slate-6"
            />
            <span
              v-for="label in contactLabels"
              :key="label"
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium text-white"
              :style="{ backgroundColor: labelColorMap[label] || '#6B7280' }"
            >
              {{ label }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="!disableExpand" class="flex flex-col items-center gap-1">
        <Button
          icon="i-lucide-chevron-down"
          variant="ghost"
          color="slate"
          size="xs"
          :class="{ 'rotate-180': isExpanded }"
          @click="onClickExpand"
        />
        <Button
          v-if="isExpanded"
          icon="i-lucide-history"
          variant="ghost"
          :color="isHistoryPanelOpen ? 'blue' : 'slate'"
          size="xs"
          @click="isHistoryPanelOpen = !isHistoryPanelOpen"
        />
      </div>

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div class="flex items-start gap-6 p-6 border-t border-n-strong">
              <div
                class="flex flex-col gap-6 min-w-0"
                :class="isHistoryPanelOpen ? 'w-1/2' : 'flex-1'"
              >
                <!-- Identifier / created at / last activity -->
                <div
                  v-if="identifier || createdAtFormatted"
                  class="flex flex-col gap-1.5"
                >
                  <span
                    v-if="identifier"
                    class="inline-flex items-center gap-1 text-sm text-n-slate-11"
                  >
                    <span class="i-ph-user-gear text-n-slate-10 size-4" />
                    {{ identifier }}
                  </span>
                  <span
                    v-if="createdAtFormatted"
                    class="inline-flex items-center gap-1 text-sm text-n-slate-11"
                  >
                    <span class="i-ph-activity text-n-slate-10 size-4" />
                    {{
                      t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', {
                        date: createdAtFormatted,
                      })
                    }}
                    •
                    {{
                      t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                        date: lastActivityAtFormatted,
                      })
                    }}
                  </span>
                </div>
                <ContactsForm
                  ref="contactsFormRef"
                  :contact-data="contactData"
                  @update="handleFormUpdate"
                />

                <div>
                  <Button
                    :label="
                      t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                    "
                    size="sm"
                    :is-loading="isUpdating"
                    :disabled="isUpdating || isFormInvalid"
                    @click="handleUpdateContact"
                  />
                </div>

                <!-- Tabs: Histórico / Empresa / Negociação / Notas -->
                <div class="flex flex-col gap-4 border-t border-n-strong pt-6">
                  <TabBar
                    :tabs="tabs"
                    :initial-active-tab="activeTabIndex"
                    class="w-full [&>button]:w-full bg-n-alpha-black2"
                    @tab-changed="handleTabChange"
                  />
                  <ContactHistory
                    v-if="activeTab === 'history'"
                    :contact-id="id"
                  />
                  <ContactCompanySection
                    v-if="activeTab === 'company'"
                    :contact-id="id"
                    :companies="companies"
                  />
                  <DealInfo
                    v-if="activeTab === 'deals'"
                    :contact-id="id"
                    @deals-changed="count => (contactDealsCount = count)"
                  />
                  <ContactNotes v-if="activeTab === 'notes'" :contact-id="id" />
                </div>
              </div>

              <div
                v-if="isHistoryPanelOpen"
                class="flex flex-col gap-3 w-1/2 min-w-0 border-l border-n-strong pl-6"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('TIMELINE.TAB_LABEL') }}
                </span>
                <Timeline subject-type="Contact" :subject-id="id" />
              </div>
            </div>
            <ContactDeleteSection
              :selected-contact="{
                id: props.id,
                name: props.name,
              }"
            />
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
